<template>
  <v-dialog v-model="dialog" max-width="720px" persistent scrollable>
    <v-card class="udf-dialog-card" rounded="lg" elevation="4">

      <!-- ── Header ─────────────────────────────────────────────────── -->
      <div class="udf-header">
        <div class="udf-header-left">
          <div class="udf-header-icon">
            <v-icon size="18" color="#3c5aaa">mdi-form-select</v-icon>
          </div>
          <div>
            <div class="udf-header-title">User Defined Fields</div>
            <div class="udf-header-sub" v-if="formConfig && formConfig.sections">
              {{ fieldCount }} {{ fieldCount === 1 ? "field" : "fields" }}
            </div>
          </div>
        </div>
        <v-btn icon variant="text" size="small" class="udf-close-btn" @click="closeDialog">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- ── Body ──────────────────────────────────────────────────── -->
      <v-card-text class="udf-body">

        <!-- Loading -->
        <div v-if="loading" class="udf-loading">
          <v-progress-circular indeterminate color="#3c5aaa" size="36" width="3" />
          <span class="udf-loading-text">Loading fields…</span>
        </div>

        <!-- Empty -->
        <div v-else-if="!formConfig || !formConfig.sections || formConfig.sections.length === 0" class="udf-empty">
          <v-icon size="48" color="#c8c8c8">mdi-text-box-outline</v-icon>
          <div class="udf-empty-text">No fields configured</div>
        </div>

        <!-- Form -->
        <v-form v-else ref="udfForm" class="udf-form">
          <template v-for="(section, si) in formConfig.sections" :key="si">
            <v-row dense>
              <v-col
                v-for="(column, ci) in section.columns"
                :key="ci"
                :cols="column.cols || 12"
                :sm="column.sm || 6"
                class="udf-field-col"
              >
                <!-- Field label -->
                <div class="udf-field-label">
                  {{ column.label }}
                  <span v-if="column.required" class="udf-required">*</span>
                </div>

                <!-- ① TextField (text / password) -->
                <UdfTextField
                  v-if="column.fieldType === 'text' || column.fieldType === 'textfield' || column.fieldType === 'password'"
                  :modelValue="formValues[column.column]"
                  :placeholder="'Enter ' + column.label"
                  :type="column.fieldType === 'password' ? 'password' : 'text'"
                  @update:modelValue="val => { formValues[column.column] = val; handleFormChanged(); }"
                />

                <!-- ② TextArea -->
                <UdfTextArea
                  v-else-if="column.fieldType === 'textArea'"
                  :modelValue="formValues[column.column]"
                  :placeholder="'Enter ' + column.label"
                  @update:modelValue="val => { formValues[column.column] = val; handleFormChanged(); }"
                />

                <!-- ③ AutoComplete (selection) -->
                <UdfAutoComplete
                  v-else-if="column.fieldType === 'selection'"
                  :modelValue="formValues[column.column]"
                  :items="column.options || []"
                  :itemTitle="column.text || 'name'"
                  :itemValue="column.itemValue || 'id'"
                  :placeholder="'Select ' + column.label"
                  :multiple="column.multiple || false"
                  @update:modelValue="val => { formValues[column.column] = val; handleFormChanged(); }"
                />

                <!-- ④ Select / Dropdown -->
                <UdfSelect
                  v-else-if="column.fieldType === 'select'"
                  :modelValue="formValues[column.column]"
                  :items="column.options || []"
                  :name="'name'"
                  :value="'id'"
                  :placeholder="'Select ' + column.label"
                  @update:modelValue="val => { formValues[column.column] = val; handleFormChanged(); }"
                />

                <!-- ⑤ DatePicker -->
                <v-text-field
                  v-else-if="column.fieldType === 'datepicker'"
                  :modelValue="formValues[column.column]"
                  type="date"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="udf-input"
                  @update:modelValue="val => { formValues[column.column] = val; handleFormChanged(); }"
                />

                <!-- ⑥ Switch -->
                <div v-else-if="column.fieldType === 'switch'" class="udf-switch-row">
                  <UdfSwitch
                    :modelValue="formValues[column.column]"
                    :label="formValues[column.column] ? 'Yes' : 'No'"
                    @update:modelValue="val => { formValues[column.column] = val; handleFormChanged(); }"
                  />
                </div>

                <!-- ⑦ CheckBox -->
                <UdfCheckBox
                  v-else-if="column.fieldType === 'checkBox'"
                  :modelValue="formValues[column.column]"
                  :label="column.label"
                  @update:modelValue="val => { formValues[column.column] = val; handleFormChanged(); }"
                />

                <!-- ⑧ RadioButton group -->
                <div v-else-if="column.fieldType === 'radioButton'" class="udf-radio-group">
                  <UdfRadioButton
                    v-for="(opt, oi) in column.options || []"
                    :key="oi"
                    :modelValue="formValues[column.column]"
                    :value="opt.id"
                    :label="opt.name"
                    :name="column.column"
                    @update:modelValue="val => { formValues[column.column] = val; handleFormChanged(); }"
                  />
                </div>

              </v-col>
            </v-row>
          </template>
        </v-form>

      </v-card-text>

      <!-- ── Footer ─────────────────────────────────────────────────── -->
      <div class="udf-footer">
        <v-btn variant="text" class="udf-btn-cancel" @click="closeDialog">Cancel</v-btn>
        <v-btn variant="flat" class="udf-btn-save" :loading="saving" :disabled="loading || saving" @click="saveUdfs">
          <v-icon size="16" class="mr-1">mdi-check</v-icon>
          Save Fields
        </v-btn>
      </div>

    </v-card>
  </v-dialog>
</template>

<script>
import UdfsService          from "@/api/service/Udfs/UdfsService";
import UdfTextField    from "../Udfs/Fields/TextFieldComponent.vue";
import UdfTextArea     from "../Udfs/Fields/TextArea.vue";
import UdfAutoComplete from "../Udfs/Fields/AutoComplete.vue";
import UdfSelect       from "../Udfs/Fields/Select.vue";
import UdfCheckBox     from "../Udfs/Fields/CheckBox.vue";
import UdfSwitch       from "../Udfs/Fields/Switch.vue";
import UdfRadioButton  from "../Udfs/Fields/RadioButton.vue";

export default {
  name: "UdfsDialog",
  components: {
    UdfTextField,
    UdfTextArea,
    UdfAutoComplete,
    UdfSelect,
    UdfCheckBox,
    UdfSwitch,
    UdfRadioButton,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    udfsFilterData: {
      type: [Array, Object],
      default: () => [],
    },
    existingValues: {
      type: Object,
      default: () => ({}),
    },
    issueData: {
      type: Object,
      default: () => ({}),
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "save", "formChanged"],
  data() {
    return {
      dialog: false,
      loading: false,
      formConfig: null,
      formValues: {},
    };
  },
  computed: {
    fieldCount() {
      if (!this.formConfig || !this.formConfig.sections) return 0;
      return this.formConfig.sections.reduce((sum, s) => sum + (s.columns || []).length, 0);
    },
  },
  watch: {
    modelValue(val) {
      this.dialog = val;
      if (val) this.buildForm();
    },
    dialog(val) {
      this.$emit("update:modelValue", val);
    },
    udfsFilterData(val) {
      if (val) this.buildForm();
    },
  },
  created() {
    this.dialog = this.modelValue;
    this.loadUdfsBySourceType()
    this.formValues = this.existingValues ? { ...this.existingValues } : {};
    if (this.udfsFilterData) this.buildForm();
    console.log(this.udfsFilterData,"this.udfsFilterData")
  },
  methods: {
    buildForm() {
      this.loading = true;
      try {
        const udfs = Array.isArray(this.udfsFilterData)
          ? this.udfsFilterData
          : this.udfsFilterData?.fields || [];

        this.formConfig = this.buildFormConfig(udfs);

        // Seed formValues: use udf.value (pre-loaded from API) → col.default → null
        const initialValues = {};
        if (this.formConfig) {
          this.formConfig.sections.forEach(section => {
            section.columns.forEach(col => {
              if (col.existingValue !== undefined && col.existingValue !== null) {
                initialValues[col.column] = col.existingValue;
              } else if (col.default !== undefined && col.default !== null) {
                initialValues[col.column] = col.default;
              } else {
                initialValues[col.column] = col.multiple ? [] : null;
              }
            });
          });
        }
        this.formValues = { ...initialValues };
      } catch (e) {
        console.error("Failed to build UDF form", e);
        this.formConfig = null;
        this.formValues = {};
      } finally {
        this.loading = false;
      }
    },

    async loadUdfsBySourceType(sourceType) {
      this.loading = true;
      try {
        const udfs = await UdfsService.findAll({ source_type: sourceType, active: 1 });
        this.formConfig = this.buildFormConfig(udfs);
        this.formValues = this.existingValues ? { ...this.existingValues } : {};
      } catch (e) {
        console.error("Failed to load UDFs", e);
        this.formConfig = null;
      } finally {
        this.loading = false;
      }
    },

    buildFormConfig(udfs) {
      if (!udfs || udfs.length === 0) return null;

      const columns = udfs.map((udf, index) => {
        // Build a unique key: prefer udf.key → udf.id → udf.value → fallback to index
        const uniqueKey = udf.key || udf.id || udf.value || `udf_field_${index}`;

        const col = {
          column:        String(uniqueKey),
          udfId:         udf.id,
          xrefId:        udf.xref_id || null,
          existingValue: udf.value !== undefined ? udf.value : null,
          label:         udf.label,
          fieldType:     udf.field_type,
          required:      false,
        };

        const parsedOptions = (() => {
          if (!udf.options) return [];
          try {
            return typeof udf.options === "string" ? JSON.parse(udf.options) : udf.options;
          } catch { return []; }
        })();

        const ft = (udf.field_type || "").toLowerCase().trim();

        if (ft === "text" || ft === "textfield" || ft === "password") {
          col.fieldType = ft === "password" ? "password" : "text";

        } else if (ft === "textarea") {
          col.fieldType = "textArea";

        } else if (["autocomplete", "selection", "multiselect", "auto_complete"].includes(ft)) {
          col.fieldType  = "selection";
          col.options    = parsedOptions;
          col.text       = udf.text  || "name";
          col.itemValue  = udf.source || "id";
          col.multiple   = udf.defaults === "multiple";

        } else if (ft === "select" || ft === "dropdown") {
          col.fieldType = "select";
          col.options   = parsedOptions;

        } else if (ft === "datepicker" || ft === "date") {
          col.fieldType = "datepicker";

        } else if (ft === "checkbox") {
          col.fieldType = "checkBox";

        } else if (ft === "switch") {
          col.fieldType = "switch";

        } else if (ft === "radiobutton" || ft === "radio") {
          col.fieldType = "radioButton";
          col.options   = parsedOptions;

        } else {
          col.fieldType = "text";
        }

        if (udf.defaults !== undefined && udf.defaults !== null && col.fieldType !== "selection") {
          col.default = udf.defaults;
        }

        return col;
      });

      return { withContainer: false, sections: [{ columns }] };
    },

    handleFormChanged() {
      // Emit a clean snapshot of all current values keyed by field identifier
      this.$emit("formChanged", { ...this.formValues });
    },

    setFieldValue(key, val) {
      this.formValues[key] = val;
      this.handleFormChanged();
    },

    async saveUdfs() {
      if (!this.formConfig) { this.closeDialog(); return; }

      const issueId = this.issueData?.id || null;
      const payload = [];

      this.formConfig.sections.forEach(section => {
        section.columns.forEach(col => {
          payload.push({
            issue_id: issueId,
            udf_id:   col.udfId,
            xref_id:  col.xrefId || null,
            value:    this.formValues[col.column] ?? null,
            udf_label: col.label ?? null,
          });
        });
      });

      this.$emit('save', payload);
      this.closeDialog();
    },

    closeDialog() {
      this.dialog = false;
    },
  },
}
</script>

<style scoped>
/* ── Card ─────────────────────────────────────────────────────────── */
.udf-dialog-card {
  overflow: hidden;
  border: 1px solid #e8eaf0;
}

/* ── Header ───────────────────────────────────────────────────────── */
.udf-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f0f4ff 0%, #f8f9fd 100%);
  border-bottom: 1px solid #e0e6f5;
}

.udf-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.udf-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background-color: #e8edf8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.udf-header-title {
  font-size: 15px;
  font-weight: 700;
  color: #272727;
  line-height: 1.3;
}

.udf-header-sub {
  font-size: 12px;
  color: #7a869a;
  margin-top: 1px;
}

.udf-close-btn {
  color: #7a869a !important;
}

.udf-close-btn:hover {
  background-color: #eef0f5 !important;
  color: #3c5aaa !important;
}

/* ── Body ─────────────────────────────────────────────────────────── */
.udf-body {
  padding: 20px 24px !important;
  max-height: 60vh;
  overflow-y: auto;
}

/* ── Loading ──────────────────────────────────────────────────────── */
.udf-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  gap: 12px;
}

.udf-loading-text {
  font-size: 13px;
  color: #7a869a;
}

/* ── Empty ────────────────────────────────────────────────────────── */
.udf-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  gap: 8px;
}

.udf-empty-text {
  font-size: 14px;
  color: #aab0bc;
}

/* ── Form fields ──────────────────────────────────────────────────── */
.udf-form {
  width: 100%;
}

.udf-field-col {
  padding-bottom: 4px;
}

.udf-field-label {
  font-size: 13px;
  font-weight: 600;
  color: #272727;
  margin-bottom: 5px;
}

.udf-required {
  color: #cf0707;
  padding-left: 2px;
}

.udf-input {
  font-size: 13px;
}

>>> .udf-input .v-field {
  border-radius: 8px;
  font-size: 13px;
  background-color: #f7f8fc;
}

>>> .udf-input .v-field:focus-within {
  background-color: #fff;
  box-shadow: 0 0 0 2px rgba(60, 90, 170, 0.18);
}

>>> .udf-input .v-field__outline {
  --v-field-border-opacity: 0.3;
}

/* Switch row */
.udf-switch-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 2px;
}

.udf-switch-label {
  font-size: 13px;
  color: #555;
}

/* Checkbox */
.udf-checkbox {
  padding-top: 4px;
}

/* Radio group */
.udf-radio-group {
  padding-top: 4px;
}

/* ── Footer ───────────────────────────────────────────────────────── */
.udf-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  background-color: #f8f9fd;
  border-top: 1px solid #e8eaf0;
}

.udf-btn-cancel {
  font-size: 13px;
  font-weight: 500;
  color: #7a869a;
  height: 36px;
  letter-spacing: 0;
}

.udf-btn-cancel:hover {
  background-color: #eef0f5 !important;
  color: #3c5aaa !important;
}

.udf-btn-save {
  background-color: #3c5aaa !important;
  color: #ffffff !important;
  font-size: 13px;
  font-weight: 600;
  height: 36px;
  letter-spacing: 0;
  border-radius: 8px;
  padding: 0 18px;
}

.udf-btn-save:hover {
  background-color: #2e4690 !important;
}

/* ── Scrollbar ────────────────────────────────────────────────────── */
.udf-body::-webkit-scrollbar        { width: 5px; }
.udf-body::-webkit-scrollbar-track  { background: #f1f3f8; }
.udf-body::-webkit-scrollbar-thumb  { background: rgba(60,90,170,0.2); border-radius: 8px; }
.udf-body::-webkit-scrollbar-thumb:hover { background: rgba(60,90,170,0.4); }
</style>
